import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Copy, Save, FileText, ChevronRight } from "lucide-react";
import EmptyState from "../components/EmptyState";
import CenikPicker from "../components/CenikPicker";
import { Button, Spinner } from "../components/ui/kit";
import { Okno, CilNabidkyDialog, NazevSablonyDialog, ZmenyCen } from "../components/SablonyDialogy";
import { NStatusPill } from "../lib/nabidky";
import { fmtKc } from "../lib/format";
import { fmtYmd } from "../lib/servis";
import api, { apiError } from "../lib/api";
import { Zpet, useNazevStranky, useUrlStav } from "../lib/navigace";
import Segment from "../components/Segment";
import Vyber from "../components/Vyber";

/**
 * Karta hlavního zařízení v ceníku (např. Zehnder Q350 TR Enthalpy).
 *
 * Sestava = co se k zařízení nabízí (příslušenství, drobný materiál, práce,
 * doprava) s množstvím. Z ní: šablona („Dům 140 m²“), nebo rovnou nabídka
 * u technologie klienta — ta se pak upravuje v běžném editoru nabídek.
 * Ceny řádků z ceníku jsou vždy aktuální; šablona drží jen množství.
 */

const cislo = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
// Oficiální prodejní cena z ceníku má přednost před nákup × marže.
const prodejJ = (r) => (r.cena_prodej != null ? cislo(r.cena_prodej) : cislo(r.cena_nakup) * (1 + cislo(r.marze_pct) / 100));
let klic = 0;
const novyKlic = () => `r${++klic}`;

// Sekce sestavy podle typu a významu položky.
const SEKCE = [
  ["prislusenstvi", "Příslušenství"], ["drobny", "Drobný materiál"], ["prace", "Práce"], ["ostatni", "Ostatní"],
];
function sekceRadku(r) {
  if (r.typ === "PRACE") return "prace";
  if (r.typ === "OSTATNI" || r.typ === "DOPRAVA") return "ostatni";
  return r.priority === "C" ? "drobny" : "prislusenstvi";
}

export default function ZarizeniCenik() {
  const { id } = useParams();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const cilTechnologie = search.get("technologie");
  const [data, setData] = useState(undefined);
  const [vse, setVse] = useState(false);
  const [tab, setTab] = useUrlStav("tab", "sestava");
  const [radky, setRadky] = useState([]);
  const [doprava, setDoprava] = useState({ vzdalenost_km: "", pocet_jizd: "", sazba_kc_km: "" });
  const [sablonaId, setSablonaId] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState("");
  const [zprava, setZprava] = useState("");

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/api/cenik/zarizeni/${id}`, { params: { vse } }); setData(d); return d; }
    catch { setData(null); return null; }
  }, [id, vse]);

  // Řádek sestavy z položky ceníku (cena, marže a DPH aktuální z ceníku).
  const zCeniku = (p, mnozstvi = "") => ({
    k: novyKlic(), polozka_id: p.id, nazev: p.name, typ: p.typ === "DOPRAVA" ? "OSTATNI" : (p.typ || "MATERIAL"),
    priority: p.priority, mj: p.mj || "ks", cena_nakup: p.cena_nakup ?? null, marze_pct: p.vychozi_marze_pct || 0, cena_prodej: p.cena_prodej ?? null,
    dph: p.vychozi_dph_pct ?? 21, mnozstvi: !mnozstvi || Number(mnozstvi) === 0 ? "" : String(mnozstvi),
  });

  const zeSablony = (d, s) => {
    const out = [];
    for (const r of s?.radky || []) {
      if (r.polozka_id) {
        const p = d.polozky[r.polozka_id];
        if (p) out.push(zCeniku(p, r.mnozstvi));
      } else {
        out.push({ k: novyKlic(), polozka_id: null, nazev: r.nazev, typ: r.typ || "PRACE", priority: null, mj: r.mj || "ks",
          cena_nakup: r.cena_nakup || 0, marze_pct: r.marze_pct || 0, dph: 21, mnozstvi: r.mnozstvi ? String(r.mnozstvi) : "" });
      }
    }
    if (!out.some((r) => r.polozka_id === d.polozka.id)) out.unshift(zCeniku(d.polozka, 1));
    return out;
  };
  // Základní sestava = komponenty do každé nabídky (i bez počtů); karta s ní začíná.
  const zakladni = (d) => (d.polozka.zakladni_sestava ? zeSablony(d, d.polozka.zakladni_sestava) : [zCeniku(d.polozka, 1)]);
  const pouzijZakladni = (d) => { setRadky(zakladni(d)); setDoprava(dopravaZ(d.polozka.zakladni_sestava?.doprava)); setSablonaId(null); };
  const dopravaZ = (s) => ({ vzdalenost_km: s?.vzdalenost_km ? String(s.vzdalenost_km) : "",
    pocet_jizd: s?.pocet_jizd ? String(s.pocet_jizd) : "", sazba_kc_km: s?.sazba_kc_km ? String(s.sazba_kc_km) : "" });

  const pouzijSablonu = (d, s) => { setRadky(zeSablony(d, s)); setDoprava(dopravaZ(s?.doprava)); setSablonaId(s?.id || null); setTab("sestava"); };

  useEffect(() => {
    load().then((d) => {
      if (!d) return;
      const s = d.sablony.find((x) => x.id === search.get("sablona"));
      if (s) pouzijSablonu(d, s); else pouzijZakladni(d);
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) load(); }, [vse]); // eslint-disable-line react-hooks/exhaustive-deps

  useNazevStranky(data?.polozka?.name);

  const souhrn = useMemo(() => {
    let nakup = 0, prodej = 0, dph = 0, vyplneno = 0;
    for (const r of radky) {
      const n = cislo(r.mnozstvi);
      if (n <= 0) continue;
      vyplneno += 1;
      nakup += n * cislo(r.cena_nakup);
      const pr = n * prodejJ(r);
      prodej += pr;
      dph += pr * (cislo(r.dph) / 100);
    }
    const dop = cislo(doprava.vzdalenost_km) * cislo(doprava.pocet_jizd) * cislo(doprava.sazba_kc_km);
    return { nakup, prodej: prodej + dop, dph: dph + dop * 0.21, doprava: dop, vyplneno };
  }, [radky, doprava]);

  if (data === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (data === null) return <div className="card p-8"><EmptyState icon="PackageX" title="Zařízení nenalezeno"
    description="Kartu má jen hlavní zařízení (význam A)." action={<Button variant="ghost" onClick={() => nav("/polozky")}>Zpět do ceníku</Button>} /></div>;

  const p = data.polozka;
  const sablona = data.sablony.find((s) => s.id === sablonaId);
  const zmenRadek = (k, patch) => setRadky((rs) => rs.map((r) => (r.k === k ? { ...r, ...patch } : r)));
  const odeber = (k) => setRadky((rs) => rs.filter((r) => r.k !== k));
  const pridejZCeniku = (polozka) => {
    if (radky.some((r) => r.polozka_id === polozka.id)) { setDlg(null); return; }
    setRadky((rs) => [...rs, zCeniku(polozka)]); setDlg(null);
  };
  const vCeste = new Set(radky.map((r) => r.polozka_id).filter(Boolean));
  const casto = (data.casto || []).filter((c) => !vCeste.has(c.polozka_id) && data.polozky[c.polozka_id]);

  // Co se posílá na server: vyplněné řádky (hlavní zařízení vždy).
  const payloadRadky = () => radky
    .filter((r) => r.polozka_id === p.id || cislo(r.mnozstvi) > 0)
    .map((r) => (r.polozka_id ? { polozka_id: r.polozka_id, mnozstvi: cislo(r.mnozstvi) || (r.polozka_id === p.id ? 1 : 0) }
      : { typ: r.typ, nazev: r.nazev, mj: r.mj, mnozstvi: cislo(r.mnozstvi), cena_nakup: cislo(r.cena_nakup), marze_pct: cislo(r.marze_pct) }));
  const payloadDoprava = () => ({ vzdalenost_km: cislo(doprava.vzdalenost_km), pocet_jizd: cislo(doprava.pocet_jizd), sazba_kc_km: cislo(doprava.sazba_kc_km) });

  const akce = async (nazev, fn) => {
    setBusy(nazev); setChyba(""); setZprava("");
    try { await fn(); } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };
  const vytvorNabidku = async (technologieId) => {
    const { data: res } = await api.post("/api/sestavy/nabidka", {
      technologie_id: technologieId, zarizeni_id: p.id, radky: payloadRadky(), doprava: payloadDoprava() });
    nav(`/nabidky/${res.nabidka_id}`);
  };
  const ulozNovou = async (nazev) => {
    const { data: s } = await api.post("/api/sablony", { zarizeni_id: p.id, nazev, radky: payloadRadky(), doprava: payloadDoprava() });
    const d = await load(); setDlg(null); if (d) pouzijSablonu(d, d.sablony.find((x) => x.id === s.id));
    setZprava(`Šablona „${nazev}“ uložena.`);
  };
  // Základní sestava drží i řádky bez počtu — počty se doplní u konkrétní nabídky.
  const ulozZakladni = () => akce("zakladni", async () => {
    await api.put(`/api/cenik/zarizeni/${p.id}/sestava`, {
      doprava: payloadDoprava(),
      radky: radky.map((r) => (r.polozka_id ? { polozka_id: r.polozka_id, mnozstvi: cislo(r.mnozstvi) }
        : { typ: r.typ, nazev: r.nazev, mj: r.mj, mnozstvi: cislo(r.mnozstvi), cena_nakup: cislo(r.cena_nakup), marze_pct: cislo(r.marze_pct) })) });
    await load(); setZprava("Základní sestava uložena. Karta se příště otevře s ní.");
  });
  const ulozZmeny = () => akce("ulozit", async () => {
    await api.patch(`/api/sablony/${sablonaId}`, { radky: payloadRadky(), doprava: payloadDoprava() });
    await load(); setZprava(`Šablona „${sablona.nazev}“ uložena.`);
  });
  const smazSablonu = () => {
    if (!window.confirm(`Smazat šablonu „${sablona.nazev}“? Nabídek, které z ní vznikly, se to netýká.`)) return;
    akce("smazat", async () => { await api.delete(`/api/sablony/${sablonaId}`); const d = await load(); if (d) pouzijZakladni(d); });
  };

  const radekView = (r) => {
    const hlavni = r.polozka_id === p.id;
    const n = cislo(r.mnozstvi);
    return (
      <div key={r.k} className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_120px_100px_110px_36px] gap-x-3 gap-y-1.5 items-center px-4 py-2.5 border-t border-line-soft"
        data-testid={`sestava-radek-${r.polozka_id || r.k}`}>
        <span className="min-w-0 col-span-2 md:col-span-1">
          <span className="block t-telo font-600 text-ink truncate">{r.nazev}</span>
          <span className="block t-meta text-muted truncate">{r.polozka_id ? "z ceníku" : "vlastní řádek"} · {fmtKc(prodejJ(r))} Kč / {r.mj}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <input className="field amount h-10 md:h-9 w-[84px] text-right" inputMode="decimal" placeholder="—"
            value={r.mnozstvi} onChange={(e) => zmenRadek(r.k, { mnozstvi: e.target.value.replace(/[^\d.,]/g, "") })}
            aria-label={`Množství ${r.nazev}`} data-testid={`sestava-mnozstvi-${r.polozka_id || r.k}`} />
          <span className="t-meta text-muted w-7">{r.mj}</span>
        </span>
        <span className="hidden md:block amount t-meta text-sec text-right">{fmtKc(prodejJ(r))}</span>
        <span className={`amount t-telo text-right ${n > 0 ? "text-ink font-600" : "text-muted"}`}>{n > 0 ? `${fmtKc(n * prodejJ(r))} Kč` : "—"}</span>
        <span className="hidden md:flex justify-end">
          {!hlavni && <button onClick={() => odeber(r.k)} aria-label="Odebrat" className="tap h-8 w-8 rounded-full text-muted hover:text-danger flex items-center justify-center"><Trash2 size={14} /></button>}
        </span>
        {!hlavni && <button onClick={() => odeber(r.k)} className="md:hidden t-meta text-muted hover:text-danger justify-self-end col-span-2">Odebrat</button>}
      </div>
    );
  };

  const hlavniRadek = radky.find((r) => r.polozka_id === p.id);
  const ostatni = radky.filter((r) => r.polozka_id !== p.id);
  const nabidkyKlientu = data.nabidky || [];

  return (
    <div data-testid="zarizeni-cenik">
      <Zpet className="mb-3" nahradni="/polozky" nahradniNazev={`Ceník${data.vyrobce.name ? ` · ${data.vyrobce.name}` : ""}`} />

      <div className="rounded-card bg-ink text-white p-5 md:p-6 mb-4">
        <div className="label-up text-white/45">{[data.vyrobce.name, data.kategorie, "hlavní zařízení"].filter(Boolean).join(" · ")}</div>
        <h1 className="text-[22px] md:text-[24px] font-800 mt-1.5 tracking-tight">{p.name}</h1>
        <div className="t-meta text-white/50 mt-0.5">{[p.katalogove_cislo, p.mj].filter(Boolean).join(" · ")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
          {[["Nákup", p.cena_nakup == null ? "—" : `${fmtKc(p.cena_nakup)} Kč`],
            ["Prodej bez DPH", `${fmtKc(prodejJ({ cena_nakup: p.cena_nakup, marze_pct: p.vychozi_marze_pct, cena_prodej: p.cena_prodej }))} Kč`],
            ["Instalováno u", `${data.instalovano.length} klientů`], ["Šablon", data.sablony.length]].map(([l, v]) => (
            <div key={l} className="rounded-icon bg-white/5 p-3 min-w-0">
              <div className="label-up text-white/40">{l}</div>
              <div className="amount t-cislo font-700 mt-1 truncate">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <Segment varianta="podtrzeni" className="flex gap-5 border-b border-line mb-4 overflow-x-auto [scrollbar-width:none]" role="tablist">
        {[["sestava", "Sestava a šablony"], ["klienti", `Klienti · ${data.instalovano.length + nabidkyKlientu.length}`]].map(([v, l]) => (
          <button data-on={tab === v} key={v} onClick={() => setTab(v)} data-testid={`zarizeni-tab-${v}`}
            className={`tap px-0.5 h-11 t-meta font-600 whitespace-nowrap ${tab === v ? "text-ink" : "text-sec hover:text-ink"}`}>{l}</button>
        ))}
      </Segment>

      {chyba && <div className="card px-4 py-3 mb-3 t-meta text-danger" data-testid="zarizeni-cenik-chyba">{chyba}</div>}
      {zprava && <div className="card px-4 py-3 mb-3 t-meta text-success" data-testid="zarizeni-cenik-zprava">{zprava}</div>}

      {tab === "sestava" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-line flex flex-wrap items-center gap-2" data-testid="sablony">
              <span className="label-up text-muted mr-1">Šablony</span>
              <button data-on={!sablonaId} onClick={() => pouzijZakladni(data)} data-testid="sestava-zakladni"
                className={`cip filtr ${!sablonaId ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                Základní sestava
              </button>
              {data.sablony.map((s) => (
                <button key={s.id} data-on={sablonaId === s.id} onClick={() => pouzijSablonu(data, s)} data-testid={`sablona-${s.id}`}
                  className={`cip filtr ${sablonaId === s.id ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                  {s.nazev}
                </button>
              ))}
            </div>
            {!sablonaId && !p.zakladni_sestava && (
              <p className="px-4 py-2.5 border-b border-line-soft bg-[#fdf3e2] t-meta text-sec" data-testid="sestava-zakladni-napoveda">
                Přidej komponenty, které se dávají do každé nabídky, a ulož je jako základní sestavu. Příště se karta otevře rovnou s nimi — jen doplníš počty.
              </p>
            )}

            <div className="hidden md:grid grid-cols-[1fr_120px_100px_110px_36px] gap-3 px-4 py-2 bg-[#fbfaf8] label-up text-muted">
              <span>Položka</span><span>Množství</span><span className="text-right">Prodej / mj</span><span className="text-right">Celkem</span><span />
            </div>
            <div className="px-4 py-1.5 bg-[#fbfaf8] border-t border-line-soft label-up text-muted">Hlavní zařízení</div>
            {hlavniRadek && radekView(hlavniRadek)}
            {SEKCE.map(([k, titul]) => {
              const rs = ostatni.filter((r) => sekceRadku(r) === k);
              if (!rs.length) return null;
              return (
                <React.Fragment key={k}>
                  <div className="px-4 py-1.5 bg-[#fbfaf8] border-t border-line-soft label-up text-muted">{titul} · {rs.length}</div>
                  {rs.map(radekView)}
                </React.Fragment>
              );
            })}

            <div className="px-4 py-3 border-t border-line-soft flex flex-wrap gap-2">
              <button onClick={() => setDlg({ typ: "picker" })} className="btn-ghost tap" data-testid="sestava-pridat"><Plus size={15} /> Přidat z ceníku</button>
              <button onClick={() => setDlg({ typ: "volny" })} className="btn-ghost tap" data-testid="sestava-volny"><Plus size={15} /> Vlastní řádek</button>
            </div>
            {casto.length > 0 && (
              <div className="px-4 pb-3" data-testid="sestava-casto">
                <div className="label-up text-muted mb-1.5">Často s tímto zařízením</div>
                <div className="flex flex-wrap gap-1.5">
                  {casto.map((c) => (
                    <button key={c.polozka_id} onClick={() => setRadky((rs) => [...rs, zCeniku(data.polozky[c.polozka_id])])}
                      title={`V ${c.pocet_nabidek} nabídkách, průměrně ${c.prumer_mnozstvi} ${c.mj}`}
                      className="tap px-3 h-9 rounded-pill border border-dashed border-line-strong t-meta font-600 text-sec hover:border-accent hover:text-ink">
                      + {c.name} <span className="text-muted">· {c.pocet_nabidek}×</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="px-4 py-3 border-t border-line-soft">
              <div className="label-up text-muted mb-1.5">Doprava</div>
              <div className="grid grid-cols-3 gap-2 max-w-[420px]">
                {[["vzdalenost_km", "km"], ["pocet_jizd", "jízd"], ["sazba_kc_km", "Kč / km"]].map(([f, l]) => (
                  <label key={f} className="block min-w-0"><span className="t-meta text-muted">{l}</span>
                    <input className="field amount w-full min-w-0" inputMode="decimal" placeholder="0" value={doprava[f]}
                      onChange={(e) => setDoprava({ ...doprava, [f]: e.target.value.replace(/[^\d.,]/g, "") })} data-testid={`sestava-doprava-${f}`} /></label>
                ))}
              </div>
            </div>
          </div>

          <div className="card p-4 space-y-2.5 lg:sticky lg:top-4" data-testid="sestava-souhrn">
            <div className="label-up text-muted">{sablona ? `Šablona ${sablona.nazev}` : "Sestava"}</div>
            {[["Vyplněno řádků", `${souhrn.vyplneno} / ${radky.length}`], ["Nákup", `${fmtKc(souhrn.nakup)} Kč`],
              ["Marže", `${fmtKc(souhrn.prodej - souhrn.doprava - souhrn.nakup)} Kč`], ["Doprava", `${fmtKc(souhrn.doprava)} Kč`],
              ["DPH", `${fmtKc(souhrn.dph)} Kč`]].map(([l, v]) => (
              <div key={l} className="flex justify-between t-telo"><span className="text-sec">{l}</span><span className="amount font-600 text-ink">{v}</span></div>
            ))}
            <div className="rounded-icon bg-ink text-white px-4 py-3 flex items-baseline justify-between">
              <span className="label-up text-white/50">Celkem s DPH</span>
              <span className="amount text-[20px] font-800">{fmtKc(souhrn.prodej + souhrn.dph)}</span>
            </div>
            <p className="t-meta text-muted">Orientačně. Přesný výpočet DPH a zaokrouhlení udělá editor nabídky.</p>
            <Button className="w-full justify-center" disabled={!!busy || souhrn.vyplneno === 0} data-testid="sestava-vytvorit"
              onClick={() => (cilTechnologie ? akce("nabidka", () => vytvorNabidku(cilTechnologie)) : setDlg({ typ: "cil" }))}>
              {busy === "nabidka" ? <Spinner /> : <><FileText size={15} /> {cilTechnologie ? "Vytvořit nabídku klientovi" : "Vytvořit nabídku…"}</>}
            </Button>
            {!sablona && (
              <button onClick={ulozZakladni} disabled={!!busy} className="btn-ghost tap w-full justify-center" data-testid="sestava-ulozit-zakladni">
                {busy === "zakladni" ? <Spinner /> : <><Save size={15} /> Uložit základní sestavu</>}
              </button>
            )}
            {sablona && (
              <button onClick={ulozZmeny} disabled={!!busy} className="btn-ghost tap w-full justify-center" data-testid="sablona-ulozit-zmeny">
                {busy === "ulozit" ? <Spinner /> : <><Save size={15} /> Uložit změny šablony</>}
              </button>
            )}
            <button onClick={() => setDlg({ typ: "nazev" })} disabled={!!busy} className="btn-ghost tap w-full justify-center" data-testid="sablona-nova">
              <Plus size={15} /> {sablona ? "Uložit jako novou šablonu" : "Uložit jako šablonu"}
            </button>
            {sablona && (
              <div className="flex flex-wrap justify-between gap-2 pt-1">
                <button onClick={() => setDlg({ typ: "kopie" })} className="tap t-meta font-600 text-sec hover:text-ink inline-flex items-center gap-1" data-testid="sablona-kopie">
                  <Copy size={13} /> Kopírovat k jinému zařízení</button>
                <button onClick={smazSablonu} className="tap t-meta font-600 text-danger hover:underline" data-testid="sablona-smazat">Smazat šablonu</button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "klienti" && (
        <div className="space-y-4">
          <div className="card overflow-hidden" data-testid="zarizeni-nabidky">
            <div className="px-4 py-3 border-b border-line flex flex-wrap items-center gap-2">
              <h2 className="t-sekce font-700 text-ink">Nabídky s tímto zařízením</h2>
              <label className="ml-auto flex items-center gap-2 t-meta text-sec">
                <input type="checkbox" checked={vse} onChange={(e) => setVse(e.target.checked)} data-testid="nabidky-vse" /> i zamítnuté
              </label>
            </div>
            {!nabidkyKlientu.length && <p className="px-4 py-5 t-meta text-muted">Zatím žádná nabídka s tímto zařízením.</p>}
            {nabidkyKlientu.map((n) => (
              <div key={n.nabidka_id} className="px-4 py-3 border-t border-line-soft first:border-0 flex flex-col md:flex-row md:items-center gap-2 md:gap-4"
                data-testid={`zarizeni-nabidka-${n.nabidka_id}`}>
                <button onClick={() => nav(`/nabidky/${n.nabidka_id}`)} className="tap min-w-0 flex-1 text-left">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="t-telo font-700 text-ink">{n.client_name || "—"}</span>
                    <NStatusPill status={n.status} />
                  </span>
                  <span className="block t-meta text-muted amount">{n.cislo} · verze {n.verze} · {n.pripad_cislo} · {n.pocet_radku} řádků · {fmtKc(n.celkem_s_dph)} Kč s DPH</span>
                </button>
                <div className="flex flex-wrap gap-2 md:shrink-0">
                  <button onClick={() => setDlg({ typ: "kopieNabidky", nabidka: n })} className="btn-ghost tap" data-testid={`nabidka-kopie-${n.nabidka_id}`}>
                    <Copy size={14} /> Použít pro jiného klienta</button>
                  <button onClick={() => setDlg({ typ: "nabidkaSablona", nabidka: n })} className="btn-ghost tap" data-testid={`nabidka-sablona-${n.nabidka_id}`}>
                    <Save size={14} /> Uložit jako šablonu</button>
                </div>
              </div>
            ))}
          </div>

          <div className="card overflow-hidden" data-testid="zarizeni-instalovano">
            <div className="px-4 py-3 border-b border-line"><h2 className="t-sekce font-700 text-ink">Instalováno u klientů</h2></div>
            {!data.instalovano.length && <p className="px-4 py-5 t-meta text-muted">Zatím u nikoho v servisní péči.</p>}
            {data.instalovano.map((z) => (
              <button key={z.zarizeni_id} onClick={() => nav(`/servis/zarizeni/${z.zarizeni_id}`)}
                className="tap w-full text-left px-4 py-3 border-t border-line-soft first:border-0 flex items-center gap-3 hover:bg-inputbg">
                <span className="min-w-0 flex-1">
                  <span className="block t-telo font-700 text-ink truncate">{z.client_name || "—"}</span>
                  <span className="block t-meta text-muted">{z.nazev}{z.datum_zprovozneni ? ` · v provozu od ${fmtYmd(z.datum_zprovozneni)}` : ""}</span>
                </span>
                <ChevronRight size={16} className="text-muted shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {dlg?.typ === "picker" && <CenikPicker kategorieId={null} vyrobceId={data.vyrobce.id} onAdd={pridejZCeniku} onClose={() => setDlg(null)} />}
      {dlg?.typ === "volny" && <VolnyRadekDialog onClose={() => setDlg(null)}
        onPridat={(r) => { setRadky((rs) => [...rs, { k: novyKlic(), polozka_id: null, priority: null, dph: 21, ...r }]); setDlg(null); }} />}
      {dlg?.typ === "nazev" && <NazevSablonyDialog onClose={() => setDlg(null)} onUlozit={ulozNovou}
        popis={`Uloží se ${souhrn.vyplneno} vyplněných řádků i s množstvím a doprava. Ceny se při použití vezmou aktuální z ceníku.`} />}
      {dlg?.typ === "cil" && <CilNabidkyDialog titulek="Vytvořit nabídku" kategorieId={data.kategorie_id}
        popis="Vyber klienta a jeho technologii. Když už nabídku má, sestava se přidá jako nová verze."
        onClose={() => setDlg(null)} onVyber={(c) => vytvorNabidku(c.technologie_id)} />}
      {dlg?.typ === "kopie" && <KopieSablonyDialog sablona={sablona} zarizeniId={p.id} onClose={() => setDlg(null)}
        onHotovo={(s) => nav(`/cenik/zarizeni/${s.zarizeni_id}?sablona=${s.id}`)} />}
      {dlg?.typ === "kopieNabidky" && <CilNabidkyDialog titulek={`Použít nabídku ${dlg.nabidka.cislo} pro jiného klienta`}
        kategorieId={data.kategorie_id} popis="Množství zůstanou, ceny se vezmou aktuální z ceníku."
        onClose={() => setDlg(null)}
        onVyber={async (c) => {
          const { data: res } = await api.post(`/api/nabidky/${dlg.nabidka.nabidka_id}/kopie`, { technologie_id: c.technologie_id });
          setDlg({ typ: "kopieHotovo", res, klient: c.client_name });
        }} />}
      {dlg?.typ === "kopieHotovo" && (
        <Okno titulek={`Nabídka ${dlg.res.cislo} pro ${dlg.klient}`} onClose={() => { setDlg(null); load(); }} testid="kopie-hotovo"
          paticka={<Button onClick={() => nav(`/nabidky/${dlg.res.nabidka_id}`)} data-testid="kopie-otevrit">Otevřít nabídku</Button>}>
          <p className="t-telo text-sec">{dlg.res.nova_nabidka ? "Nová nabídka je založená." : `Přidáno jako verze ${dlg.res.verze}.`}</p>
          <ZmenyCen zmeny={dlg.res.zmeny_cen} />
        </Okno>
      )}
      {dlg?.typ === "nabidkaSablona" && <NazevSablonyDialog onClose={() => setDlg(null)}
        popis={`Šablona vznikne z nabídky ${dlg.nabidka.cislo} (${dlg.nabidka.client_name}) i s množstvím.`}
        onUlozit={async (nazev) => {
          const { data: s } = await api.post(`/api/nabidky/${dlg.nabidka.nabidka_id}/sablona`, { nazev, zarizeni_id: p.id });
          const d = await load(); setDlg(null);
          if (d) pouzijSablonu(d, d.sablony.find((x) => x.id === s.id));
          setZprava(`Šablona „${nazev}“ uložena.`);
        }} />}
    </div>
  );
}

/** Řádek mimo ceník (typicky práce): název, jednotka, nákup a marže. */
function VolnyRadekDialog({ onClose, onPridat }) {
  const [f, setF] = useState({ typ: "PRACE", nazev: "", mj: "h", mnozstvi: "", cena_nakup: "", marze_pct: "" });
  const [chyba, setChyba] = useState("");
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pridat = () => {
    if (!f.nazev.trim()) return setChyba("Zadej název řádku.");
    onPridat({ ...f, nazev: f.nazev.trim(), cena_nakup: cislo(f.cena_nakup), marze_pct: cislo(f.marze_pct) });
  };
  return (
    <Okno titulek="Vlastní řádek" onClose={onClose} testid="volny-radek"
      paticka={<><button onClick={onClose} className="btn-ghost tap">Zrušit</button><Button onClick={pridat} data-testid="volny-pridat">Přidat</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
        <label className="block min-w-0"><span className="label-up text-muted block mb-1">Název</span>
          <input className="field w-full min-w-0" value={f.nazev} onChange={up("nazev")} placeholder="Montáž a zaregulování" autoFocus data-testid="volny-nazev" /></label>
        <label className="block min-w-0"><span className="label-up text-muted block mb-1">Druh</span>
          <Vyber className="field w-full min-w-0" value={f.typ} onChange={up("typ")}>
            <option value="PRACE">Práce</option><option value="MATERIAL">Materiál</option><option value="OSTATNI">Ostatní</option>
          </Vyber></label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["mnozstvi", "Množství"], ["mj", "MJ"], ["cena_nakup", "Nákup Kč"], ["marze_pct", "Marže %"]].map(([k, l]) => (
          <label key={k} className="block min-w-0"><span className="label-up text-muted block mb-1">{l}</span>
            <input className="field amount w-full min-w-0" value={f[k]} onChange={up(k)} inputMode={k === "mj" ? undefined : "decimal"} /></label>
        ))}
      </div>
      {chyba && <p className="t-meta text-danger">{chyba}</p>}
    </Okno>
  );
}

/** Šablonu k jinému hlavnímu zařízení — příslušenství zůstane, jednotka se vymění. */
function KopieSablonyDialog({ sablona, zarizeniId, onClose, onHotovo }) {
  const [items, setItems] = useState(null);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    api.get("/api/polozky", { params: { limit: 500, servis: "bez" } })
      .then(({ data }) => setItems(data.items.filter((x) => x.priority === "A" && x.typ === "MATERIAL" && x.id !== zarizeniId)))
      .catch(() => setItems([]));
  }, [zarizeniId]);
  const kopie = async (cil) => {
    setChyba("");
    try { const { data } = await api.post(`/api/sablony/${sablona.id}/kopie`, { zarizeni_id: cil.id }); onHotovo(data); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <Okno titulek={`Kopírovat šablonu „${sablona.nazev}“`} onClose={onClose} testid="kopie-sablony">
      <p className="t-meta text-muted">Vyber hlavní zařízení, ke kterému se šablona zkopíruje. Příslušenství a množství zůstanou.</p>
      {chyba && <p className="t-meta text-danger">{chyba}</p>}
      {items === null ? <div className="py-6 flex justify-center"><Spinner /></div> : (
        <div className="border border-line-softer rounded-card overflow-hidden">
          {items.map((x) => (
            <button key={x.id} onClick={() => kopie(x)} data-testid={`kopie-cil-${x.id}`}
              className="tap w-full text-left px-4 py-2.5 border-t border-line-softer first:border-0 hover:bg-inputbg t-telo font-600 text-ink">{x.name}</button>
          ))}
          {!items.length && <p className="px-4 py-3 t-meta text-muted">V ceníku není jiné hlavní zařízení.</p>}
        </div>
      )}
    </Okno>
  );
}
